import type { ReactNode } from "react";
import LoginForm from "@/components/auth/login/login-form";
import { AmnealLogo } from "@/components/general/amneal-logo";
import { AirVent, ShieldCheck } from "lucide-react";

type LoginPageShellProps = {
  children?: ReactNode;
};

export default function LoginPageShell({ children }: LoginPageShellProps) {
  return (
    <div className="relative flex min-h-screen flex-col bg-background">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom_right,oklch(0.97_0.01_279)_0%,var(--background)_45%,oklch(0.985_0.005_279)_100%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,oklch(0.85_0_0/0.35)_1px,transparent_0)] [background-size:24px_24px]" />
        <div className="absolute -top-32 left-1/2 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-primary/10 blur-[120px]" />
        <div className="absolute right-0 -bottom-32 h-80 w-80 rounded-full bg-primary/5 blur-[100px]" />
      </div>

      <header className="relative z-10 flex items-center justify-between px-6 py-5 sm:px-10">
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-md bg-primary shadow-md shadow-primary/20">
            <AirVent className="size-5 text-primary-foreground" />
          </div>
          <div className="grid text-sm leading-tight">
            <span className="font-semibold tracking-tight text-foreground">
              Smoke Study
            </span>
            <span className="text-xs text-primary">AI powered analysis</span>
          </div>
        </div>
        <AmnealLogo className="hidden sm:block" priority />
      </header>

      <main className="relative z-10 flex flex-1 items-center justify-center px-6 py-8 sm:px-10">
        <div className="w-full max-w-md">
          <h1 className="mb-6 text-center text-[20px] font-bold leading-snug tracking-tight text-foreground">
            AI-Powered Quality Assurance for Sterile
          </h1>
          <div className="overflow-hidden rounded-lg border border-border/80 bg-card shadow-md shadow-primary/5">
            <div className="border-b border-border/70 bg-muted/30 px-8 py-7 text-center">
              <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-lg bg-primary/10 ring-1 ring-primary/15">
                <AirVent className="size-7 text-primary" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-[1.75rem]">
                Sign in to continue
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Access smoke study analytics, monitoring, and compliance
                reporting for your organization.
              </p>
            </div>

            <div className="space-y-6 px-8 py-7">
              <div className="flex items-start gap-3 rounded-md bg-muted/20 p-4">
                <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary/10">
                  <ShieldCheck className="size-5 text-primary" />
                </div>
                <div className="min-w-0 text-left">
                  <p className="text-sm font-semibold text-foreground">
                    Microsoft Single Sign-On
                  </p>
                  <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                    Authenticate with your corporate account via Microsoft Entra
                    ID. No separate password required.
                  </p>
                </div>
              </div>

              {children ?? <LoginForm />}

              <p className="text-center text-xs leading-relaxed text-muted-foreground">
                By signing in, you confirm access is limited to authorized
                personnel and governed by your organization&apos;s policies.
              </p>
            </div>
          </div>
        </div>
      </main>

      <footer className="relative z-10 px-6 py-6 sm:px-10">
        <div className="mx-auto flex max-w-md flex-col items-center gap-3 text-center sm:max-w-none sm:flex-row sm:justify-between">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} AI powered smoke study analysis. All rights
            reserved. @{" "}
            <a
              href="https://www.iqudtek.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium transition-colors hover:text-foreground"
            >
              iQudTek
            </a>
          </p>
          {/* <div className="flex items-center gap-4 text-xs font-medium text-muted-foreground">
            <a href="#" className="transition-colors hover:text-foreground">
              Privacy Policy
            </a>
            <span className="size-1 rounded-full bg-border" />
            <a href="#" className="transition-colors hover:text-foreground">
              Terms of Service
            </a>
            <span className="size-1 rounded-full bg-border" />
            <a href="#" className="transition-colors hover:text-foreground">
              Support
            </a>
          </div> */}
        </div>
      </footer>
    </div>
  );
}
